import _slicedToArray from "/codebuild/output/src3305341033/src/hvorigins/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _regeneratorRuntime from "/codebuild/output/src3305341033/src/hvorigins/node_modules/@babel/runtime/regenerator";

var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

import { WalletNotConnectedError, WalletNotReadyError } from '@solana/wallet-adapter-base';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WalletNotSelectedError } from './errors';
import { useLocalStorage } from './useLocalStorage';
import { WalletContext } from './useWallet';
var initialState = {
  wallet: null,
  adapter: null,
  ready: false,
  publicKey: null,
  connected: false
};
export var WalletProvider = function WalletProvider(_ref) {
  var children = _ref.children,
      wallets = _ref.wallets,
      _ref$autoConnect = _ref.autoConnect,
      autoConnect = _ref$autoConnect === void 0 ? false : _ref$autoConnect,
      _ref$onError = _ref.onError,
      onError = _ref$onError === void 0 ? function (error) {
    return console.error(error);
  } : _ref$onError,
      _ref$localStorageKey = _ref.localStorageKey,
      localStorageKey = _ref$localStorageKey === void 0 ? 'walletName' : _ref$localStorageKey;

  var _useLocalStorage = useLocalStorage(localStorageKey, null),
      _useLocalStorage2 = _slicedToArray(_useLocalStorage, 2),
      name = _useLocalStorage2[0],
      setName = _useLocalStorage2[1];

  var _useState = useState(initialState),
      _useState2 = _slicedToArray(_useState, 2),
      _useState2$ = _useState2[0],
      wallet = _useState2$.wallet,
      adapter = _useState2$.adapter,
      ready = _useState2$.ready,
      publicKey = _useState2$.publicKey,
      connected = _useState2$.connected,
      setState = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      connecting = _useState4[0],
      setConnecting = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      disconnecting = _useState6[0],
      setDisconnecting = _useState6[1];

  var isConnecting = useRef(false);
  var isDisconnecting = useRef(false); // Map of wallet names to wallets

  var walletsByName = useMemo(function () {
    return wallets.reduce(function (walletsByName, wallet) {
      walletsByName[wallet.name] = wallet;
      return walletsByName;
    }, {});
  }, [wallets]); // When the selected wallet changes, initialize the state

  useEffect(function () {
    var wallet = name && walletsByName[name] || null;
    var adapter = wallet && wallet.adapter();

    if (adapter) {
      var _ready = adapter.ready,
          _publicKey = adapter.publicKey,
          _connected = adapter.connected;
      setState({
        wallet: wallet,
        adapter: adapter,
        connected: _connected,
        publicKey: _publicKey,
        ready: _ready
      });
    } else {
      setState(initialState);
    }
  }, [name, walletsByName, setState]); // If autoConnect is enabled, try to connect when the adapter changes and is ready

  useEffect(function () {
    if (isConnecting.current || connecting || connected || !autoConnect || !adapter || !ready) return;

    (function () {
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                isConnecting.current = true;
                setConnecting(true);
                _context.prev = 2;
                _context.next = 5;
                return adapter.connect();

              case 5:
                _context.next = 10;
                break;

              case 7:
                _context.prev = 7;
                _context.t0 = _context["catch"](2);
                // Clear the selected wallet
                setName(null); // Don't throw error, but onError will still be called

              case 10:
                _context.prev = 10;
                setConnecting(false);
                isConnecting.current = false;
                return _context.finish(10);

              case 14:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[2, 7, 10, 14]]);
      }));
    })();
  }, [isConnecting, connecting, connected, autoConnect, adapter, ready, setConnecting, setName]); // Select a wallet by name

  var select = useCallback(function (newName) {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (!(name === newName)) {
                _context2.next = 2;
                break;
              }

              return _context2.abrupt("return");

            case 2:
              if (!adapter) {
                _context2.next = 5;
                break;
              }

              _context2.next = 5;
              return adapter.disconnect();

            case 5:
              setName(newName);

            case 6:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));
  }, [name, adapter, setName]); // Handle the adapter's ready event

  var onReady = useCallback(function () {
    return setState(function (state) {
      return Object.assign(Object.assign({}, state), {
        ready: true
      });
    });
  }, [setState]); // Handle the adapter's connect event

  var onConnect = useCallback(function () {
    if (!adapter) return;
    var connected = adapter.connected,
        publicKey = adapter.publicKey,
        ready = adapter.ready;
    setState(function (state) {
      return Object.assign(Object.assign({}, state), {
        connected: connected,
        publicKey: publicKey,
        ready: ready
      });
    });
  }, [adapter, setState]); // Handle the adapter's disconnect event

  var onDisconnect = useCallback(function () {
    return setName(null);
  }, [setName]); // Connect the adapter to the wallet

  var connect = useCallback(function () {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
      var error, _error;

      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              if (!(isConnecting.current || connecting || disconnecting || connected)) {
                _context3.next = 2;
                break;
              }

              return _context3.abrupt("return");

            case 2:
              if (!(!wallet || !adapter)) {
                _context3.next = 6;
                break;
              }

              error = new WalletNotSelectedError();
              onError(error);
              throw error;

            case 6:
              if (ready) {
                _context3.next = 12;
                break;
              }

              setName(null);

              if (typeof window !== 'undefined') {
                window.open(wallet.url, '_blank');
              }

              _error = new WalletNotReadyError();
              onError(_error);
              throw _error;

            case 12:
              isConnecting.current = true;
              setConnecting(true);
              _context3.prev = 14;
              _context3.next = 17;
              return adapter.connect();

            case 17:
              _context3.next = 23;
              break;

            case 19:
              _context3.prev = 19;
              _context3.t0 = _context3["catch"](14);
              setName(null);
              throw _context3.t0;

            case 23:
              _context3.prev = 23;
              setConnecting(false);
              isConnecting.current = false;
              return _context3.finish(23);

            case 27:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, null, [[14, 19, 23, 27]]);
    }));
  }, [isConnecting, connecting, disconnecting, connected, wallet, adapter, onError, ready, setConnecting, setName]); // Disconnect the adapter from the wallet

  var disconnect = useCallback(function () {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              if (!(isDisconnecting.current || disconnecting)) {
                _context4.next = 2;
                break;
              }

              return _context4.abrupt("return");

            case 2:
              if (adapter) {
                _context4.next = 4;
                break;
              }

              return _context4.abrupt("return", setName(null));

            case 4:
              isDisconnecting.current = true;
              setDisconnecting(true);
              _context4.prev = 6;
              _context4.next = 9;
              return adapter.disconnect();

            case 9:
              _context4.prev = 9;
              setName(null);
              setDisconnecting(false);
              isDisconnecting.current = false;
              return _context4.finish(9);

            case 14:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, null, [[6,, 9, 14]]);
    }));
  }, [isDisconnecting, disconnecting, adapter, setDisconnecting, setName]); // Send a transaction using the provided connection

  var sendTransaction = useCallback(function (transaction, connection, options) {
    return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee5() {
      var error, _error2;

      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              if (adapter) {
                _context5.next = 4;
                break;
              }

              error = new WalletNotSelectedError();
              onError(error);
              throw error;

            case 4:
              if (connected) {
                _context5.next = 8;
                break;
              }

              _error2 = new WalletNotConnectedError();
              onError(_error2);
              throw _error2;

            case 8:
              _context5.next = 10;
              return adapter.sendTransaction(transaction, connection, options);

            case 10:
              return _context5.abrupt("return", _context5.sent);

            case 11:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5);
    }));
  }, [adapter, onError, connected]); // Sign a transaction if the wallet supports it

  var signTransaction = useMemo(function () {
    return adapter && 'signTransaction' in adapter ? function (transaction) {
      return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee6() {
        var error;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) {
            switch (_context6.prev = _context6.next) {
              case 0:
                if (connected) {
                  _context6.next = 4;
                  break;
                }

                error = new WalletNotConnectedError();
                onError(error);
                throw error;

              case 4:
                _context6.next = 6;
                return adapter.signTransaction(transaction);

              case 6:
                return _context6.abrupt("return", _context6.sent);

              case 7:
              case "end":
                return _context6.stop();
            }
          }
        }, _callee6);
      }));
    } : undefined;
  }, [adapter, onError, connected]); // Sign multiple transactions if the wallet supports it

  var signAllTransactions = useMemo(function () {
    return adapter && 'signAllTransactions' in adapter ? function (transactions) {
      return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee7() {
        var error;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) {
            switch (_context7.prev = _context7.next) {
              case 0:
                if (connected) {
                  _context7.next = 4;
                  break;
                }

                error = new WalletNotConnectedError();
                onError(error);
                throw error;

              case 4:
                _context7.next = 6;
                return adapter.signAllTransactions(transactions);

              case 6:
                return _context7.abrupt("return", _context7.sent);

              case 7:
              case "end":
                return _context7.stop();
            }
          }
        }, _callee7);
      }));
    } : undefined;
  }, [adapter, onError, connected]); // Sign an arbitrary message if the wallet supports it

  var signMessage = useMemo(function () {
    return adapter && 'signMessage' in adapter ? function (message) {
      return __awaiter(void 0, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee8() {
        var error;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) {
            switch (_context8.prev = _context8.next) {
              case 0:
                if (connected) {
                  _context8.next = 4;
                  break;
                }

                error = new WalletNotConnectedError();
                onError(error);
                throw error;

              case 4:
                _context8.next = 6;
                return adapter.signMessage(message);

              case 6:
                return _context8.abrupt("return", _context8.sent);

              case 7:
              case "end":
                return _context8.stop();
            }
          }
        }, _callee8);
      }));
    } : undefined;
  }, [adapter, onError, connected]); // Setup and teardown event listeners when the adapter changes

  useEffect(function () {
    if (adapter) {
      adapter.on('ready', onReady);
      adapter.on('connect', onConnect);
      adapter.on('disconnect', onDisconnect);
      adapter.on('error', onError);
      return function () {
        adapter.off('ready', onReady);
        adapter.off('connect', onConnect);
        adapter.off('disconnect', onDisconnect);
        adapter.off('error', onError);
      };
    }
  }, [adapter, onReady, onConnect, onDisconnect, onError]);
  return React.createElement(WalletContext.Provider, {
    value: {
      wallets: wallets,
      autoConnect: autoConnect,
      wallet: wallet,
      adapter: adapter,
      publicKey: publicKey,
      ready: ready,
      connected: connected,
      connecting: connecting,
      disconnecting: disconnecting,
      select: select,
      connect: connect,
      disconnect: disconnect,
      sendTransaction: sendTransaction,
      signTransaction: signTransaction,
      signAllTransactions: signAllTransactions,
      signMessage: signMessage
    }
  }, children);
};